import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from '@/components/ui/accordion.tsx';
import { RadioGroup, RadioGroupItem } from '@/components/ui/radio-group.tsx';
import { Label } from '@/components/ui/label.tsx';
import { cn } from '@/lib/utils.ts';
import { Tooltip, TooltipContent, TooltipTrigger } from '@/components/ui/tooltip.tsx';
import { useEffect, useMemo, useState } from 'react';
import { TUseSearchParams } from '@/hooks/use-search-params.ts';
import { FacetCheckboxes } from '@/app/products/list/facet-checkboxes.tsx';
import { IProductsRes, TFacet } from '@/types/models.ts';
import { FacetSize } from '@/app/products/list/facet-size.tsx';

type Props = {
  data?: IProductsRes;
  useSearch: TUseSearchParams;
  isRugsPage: boolean;
}
export function FacetsSidebar({ data, useSearch, isRugsPage }: Props) {
  const [type, setType] = useState(String(useSearch.get('type')));
  const onTypeChange = (value: string) => {
    setType(value);
    useSearch.setParams({ type: value });
  };

  useEffect(() => {
    setType(String(useSearch.get('type')));
  }, [useSearch.params]);

  const handleColorClick = (color: TFacet) => {
    useSearch.setParams({ c_p: color.value });
  };
  const facetKeys = useMemo(() => {
    return isRugsPage ? ['c_p', 'coll_id', 'category', 'size'] : ['type', 'c_p', 'construction', 'pattern', 'mat_cat'];
  }, [isRugsPage]);

  const key = useMemo(() => {
    return facetKeys.join('-');
  }, [facetKeys]);

  return (
    <Accordion key={key} type="multiple" className="w-full" defaultValue={facetKeys}>
      {facetKeys.includes('type') && (
        <AccordionItem value="type">
          <AccordionTrigger className="cursor-pointer">Type</AccordionTrigger>
          <AccordionContent>
            <RadioGroup value={type} onValueChange={onTypeChange}>
              {data?.facets?.type?.map((type) => (
                <div key={type.value} className="flex items-center">
                  <RadioGroupItem value={String(type.value)} id={`type-radio-${type.value}`} />
                  <Label htmlFor={`type-radio-${type.value}`} className="pl-2">
                    {type.name} ({type.count})
                  </Label>
                </div>
              ))}
            </RadioGroup>
          </AccordionContent>
        </AccordionItem>
      )}
      <AccordionItem value="c_p">
        <AccordionTrigger className="cursor-pointer">Color</AccordionTrigger>
        <AccordionContent className="product-color-boxes flex gap-2">
          {data?.facets?.c_p?.map((color) => (
            <Tooltip key={color.value}>
              <TooltipTrigger asChild>
                <div className={cn(' product-color-' + color.value)} onClick={() => handleColorClick(color)}></div>
              </TooltipTrigger>
              <TooltipContent>
                {color.name} - ({color.count})
              </TooltipContent>
            </Tooltip>
          ))}
        </AccordionContent>
      </AccordionItem>
      {facetKeys.includes('pattern') && (
        <AccordionItem value="pattern">
          <AccordionTrigger className="cursor-pointer">Pattern</AccordionTrigger>
          <AccordionContent>
            {data?.facets?.pattern && (
              <FacetCheckboxes facets={data.facets.pattern} facetKey="pattern" useSearch={useSearch} />
            )}
          </AccordionContent>
        </AccordionItem>
      )}
      {facetKeys.includes('mat_cat') && (
        <AccordionItem value="mat_cat">
          <AccordionTrigger className="cursor-pointer">Material</AccordionTrigger>
          <AccordionContent>
            {data?.facets?.mat_cat && (
              <FacetCheckboxes facets={data.facets.mat_cat} facetKey="mat_cat" useSearch={useSearch} />
            )}
          </AccordionContent>
        </AccordionItem>
      )}
      {facetKeys.includes('construction') && (
        <AccordionItem value="construction">
          <AccordionTrigger className="cursor-pointer">Construction</AccordionTrigger>
          <AccordionContent>
            {data?.facets?.construction && (
              <FacetCheckboxes facets={data.facets.construction} facetKey="construction" useSearch={useSearch} />
            )}
          </AccordionContent>
        </AccordionItem>
      )}
      {facetKeys.includes('coll_id') && (
        <AccordionItem value="coll_id">
          <AccordionTrigger className="cursor-pointer">Collection</AccordionTrigger>
          <AccordionContent>
            {data?.facets?.coll_id && (
              <FacetCheckboxes facets={data.facets.coll_id} facetKey="coll_id" useSearch={useSearch} />
            )}
          </AccordionContent>
        </AccordionItem>
      )}
      {facetKeys.includes('category') && (
        <AccordionItem value="category">
          <AccordionTrigger className="cursor-pointer">Styles</AccordionTrigger>
          <AccordionContent>
            {data?.facets?.category && (
              <FacetCheckboxes facets={data.facets.category} facetKey="category" useSearch={useSearch} />
            )}
          </AccordionContent>
        </AccordionItem>
      )}
      {facetKeys.includes('size') && (
        <AccordionItem value="size">
          <AccordionTrigger className="cursor-pointer">Size</AccordionTrigger>
          <AccordionContent>
            <FacetSize useSearch={useSearch} />
          </AccordionContent>
        </AccordionItem>
      )}
    </Accordion>
  );
}
